import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Search,
  MapPin,
  Briefcase,
  Globe,
  Sparkles,
  RotateCcw,
  LineChart,
  TrendingUp,
  Database,
  Cpu,
  Code2,
} from 'lucide-react';
import { Reveal, PageHero, Eyebrow, CTABand, SectionHeading } from '../components/Shared';
import { jobOpenings, culturePillars, hiringProcess } from '../data/careers';
import { L } from '../i18n/LocaleProvider';

const DEPARTMENT_ICONS = {
  'Business Analyst': LineChart,
  'Business Development': TrendingUp,
  'Analytics': Database,
  'Cloud Architecture': Globe,
  'Artificial Intelligence': Cpu,
  'Engineering': Code2,
};

const getJobIcon = (job) => {
  if (job?.icon && (typeof job.icon === 'function' || typeof job.icon === 'object')) {
    return job.icon;
  }
  if (job?.department && DEPARTMENT_ICONS[job.department]) {
    return DEPARTMENT_ICONS[job.department];
  }
  return Briefcase;
};

export default function Careers() {
  const [openings, setOpenings] = useState(jobOpenings);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('All');
  const [selectedWorkplace, setSelectedWorkplace] = useState('All');
  const [selectedEmployment, setSelectedEmployment] = useState('All');

  // Load opportunities from database
  useEffect(() => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== 'undefined' && window.location.port === '3000'
        ? 'http://localhost:5000'
        : '');

    fetch(`${backendUrl}/api/careers/opportunities?status=active`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load opportunities');
        return res.json();
      })
      .then((data) => {
        if (data?.opportunities && data.opportunities.length > 0) {
          setOpenings(data.opportunities);
        }
      })
      .catch((err) => {
        console.warn('Fallback to static job openings:', err);
      });
  }, []);

  const departments = useMemo(() => {
    return ['All', ...new Set(openings.map((j) => j.department).filter(Boolean))];
  }, [openings]);

  const workplaceTypes = useMemo(() => {
    return ['All', ...new Set(openings.map((j) => j.workplaceType).filter(Boolean))];
  }, [openings]);

  const employmentTypes = useMemo(() => {
    return ['All', ...new Set(openings.map((j) => j.type).filter(Boolean))];
  }, [openings]);

  const filteredJobs = useMemo(() => {
    return openings.filter((job) => {
      const matchesSearch =
        searchTerm.trim() === '' ||
        (job.title && job.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.summary && job.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.location && job.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.department && job.department.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesDept = selectedDept === 'All' || job.department === selectedDept;
      const matchesWorkplace = selectedWorkplace === 'All' || job.workplaceType === selectedWorkplace;
      const matchesEmployment = selectedEmployment === 'All' || job.type === selectedEmployment;

      return matchesSearch && matchesDept && matchesWorkplace && matchesEmployment;
    });
  }, [openings, searchTerm, selectedDept, selectedWorkplace, selectedEmployment]);

  const hasActiveFilters =
    searchTerm.trim() !== '' || selectedDept !== 'All' || selectedWorkplace !== 'All' || selectedEmployment !== 'All';

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDept('All');
    setSelectedWorkplace('All');
    setSelectedEmployment('All');
  };

  return (
    <L>
      <main id="main-content" className="container careers-page" data-testid="careers-page">
        {/* Page Hero */}
        <PageHero
          id="careers-hero"
          eyebrow="CAREERS AT TECHSPITZE"
          lines={[{ text: 'Build what’s next.' }, { text: 'With us.', accent: true }]}
          description="Join an ambitious global team transforming enterprise technology, autonomous AI, and high-impact digital experiences."
        />

        {/* Culture & Pillars Strip */}
        <section className="careers-culture-strip" aria-label="Why TechSpitze">
          {culturePillars.map((pillar, i) => (
            <Reveal className="culture-card glass" key={pillar.title} delay={i * 0.08} data-testid={`culture-card-${i}`}>
              <span className="culture-tag">{pillar.tag}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.description}</p>
            </Reveal>
          ))}
        </section>

        {/* Open Opportunities Section */}
        <section id="open-roles" aria-label="Current Openings">
          <SectionHeading
            id="careers-list"
            eyebrow="CURRENT OPENINGS"
            title="Explore our opportunities."
            description="Find your next challenge. We hire exceptional minds across strategy, software, intelligence, and cloud."
          />

          {/* Filter & Search Bar */}
          <Reveal className="careers-filter-bar glass">
            <div className="filter-top-row">
              <div className="careers-search-wrapper">
                <Search size={18} className="search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search by role, skill, or location…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="careers-search-input"
                  data-testid="careers-search-input"
                  aria-label="Search job openings"
                />
              </div>

              <div className="filter-dropdowns">
                <select
                  value={selectedWorkplace}
                  onChange={(e) => setSelectedWorkplace(e.target.value)}
                  className="careers-select"
                  data-testid="careers-filter-workplace"
                  aria-label="Filter by workplace type"
                >
                  {workplaceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === 'All' ? 'All Workplaces' : type}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedEmployment}
                  onChange={(e) => setSelectedEmployment(e.target.value)}
                  className="careers-select"
                  data-testid="careers-filter-employment"
                  aria-label="Filter by employment type"
                >
                  {employmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === 'All' ? 'All Types' : type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="filter-bottom-row">
              <div className="department-chips" role="tablist" aria-label="Filter by department">
                {departments.map((dept) => (
                  <button
                    key={dept}
                    type="button"
                    className={`department-chip ${selectedDept === dept ? 'active' : ''}`}
                    onClick={() => setSelectedDept(dept)}
                    data-testid={`careers-dept-${dept.toLowerCase().replaceAll(' ', '-')}`}
                  >
                    {dept}
                  </button>
                ))}
              </div>

              <div className="careers-results-meta">
                <span className="active-count" data-testid="careers-count">
                  {filteredJobs.length} {filteredJobs.length === 1 ? 'opening' : 'openings'} found
                </span>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                    data-testid="careers-clear-filters"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            </div>
          </Reveal>

          {/* Openings Grid */}
          {filteredJobs.length > 0 ? (
            <div className="careers-grid" data-testid="careers-openings-list">
              {filteredJobs.map((job, index) => {
                const JobIcon = getJobIcon(job);
                return (
                  <Reveal
                    key={job.id}
                    className="career-card glass"
                    delay={index * 0.05}
                    data-testid={`career-card-${job.id}`}
                  >
                    <div className="career-card-main">
                      <div className="career-badge-row">
                        <span className="badge-department">
                          <JobIcon size={14} aria-hidden="true" />
                          <span>{job.department}</span>
                        </span>
                        <span className="badge-dot" aria-hidden="true" />
                        <span className="badge-posted">{job.postedDate || 'Active'}</span>
                      </div>

                    <h3 className="career-title" data-testid={`career-title-${job.id}`}>
                      {job.title}
                    </h3>

                    <p className="career-summary" data-testid={`career-summary-${job.id}`}>
                      {job.summary}
                    </p>

                    <div className="career-meta-row">
                      <span className="meta-pill" data-testid={`career-location-${job.id}`}>
                        <MapPin size={13} aria-hidden="true" />
                        <span>{job.location}</span>
                      </span>
                      <span className="meta-pill highlight" data-testid={`career-type-${job.id}`}>
                        <Briefcase size={13} aria-hidden="true" />
                        <span>{job.type}</span>
                      </span>
                      <span className="meta-pill" data-testid={`career-workplace-${job.id}`}>
                        <Globe size={13} aria-hidden="true" />
                        <span>{job.workplaceType}</span>
                      </span>
                      <span className="meta-pill" data-testid={`career-exp-${job.id}`}>
                        <span>{job.experience}</span>
                      </span>
                    </div>
                  </div>

                  <div className="career-card-action">
                    <Link
                      to={`/careers/${job.id}`}
                      className="details-btn"
                      data-testid={`career-details-link-${job.id}`}
                    >
                      <span>View Details</span>
                      <ArrowUpRight size={17} aria-hidden="true" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
          ) : (
            <div className="careers-empty-state glass" data-testid="careers-empty-state">
              <div className="empty-icon-wrap">
                <Sparkles size={28} />
              </div>
              <h3>No matching opportunities found</h3>
              <p>Try adjusting your search terms or clearing your department/workplace filters.</p>
              <button
                type="button"
                onClick={clearFilters}
                className="action-button button-secondary"
                data-testid="careers-reset-empty-btn"
              >
                Clear all filters <RotateCcw size={15} />
              </button>
            </div>
          )}
        </section>

        {/* How We Hire Section */}
        <section className="hiring-process-section" aria-label="Our hiring process">
          <SectionHeading
            id="hiring-process"
            eyebrow="OUR PROCESS"
            title="How we hire."
            description="Transparent, respectful of your time, and focused on real-world collaboration."
          />
          <div className="process-steps-grid">
            {hiringProcess.map((step, i) => (
              <Reveal className="process-step-card glass" key={step.step} delay={i * 0.08} data-testid={`process-step-${i}`}>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <CTABand
          id="careers-cta"
          title={
            <>
              Don’t see the right role?
              <br />
              Let’s talk anyway.
            </>
          }
        />
      </main>
    </L>
  );
}
